import React, { useEffect, useRef } from 'react';

// Sets up the starfield canvas animation. Returns a cleanup function.
// Pure DOM/canvas logic — intentionally lives outside the component.
function startStarfield(canvas) {
    const ctx = canvas.getContext('2d');
    let raf;
    let stars = [];
    const mouse = { x: 0, y: 0 };

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      const count = Math.min(220, Math.floor((canvas.width * canvas.height) / 9000));
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        z: Math.random() * 0.8 + 0.2, // depth 0.2 - 1
        r: Math.random() * 1.4 + 0.3,
        tw: Math.random() * Math.PI * 2,
        tws: Math.random() * 0.03 + 0.005,
      }));
    };

    const onMouse = (e) => {
      mouse.x = (e.clientX / window.innerWidth - 0.5) * 2;
      mouse.y = (e.clientY / window.innerHeight - 0.5) * 2;
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach((s) => {
        s.tw += s.tws;
        s.y -= 0.06 * s.z; // slow upward drift (rocket feeling)
        if (s.y < -4) {
          s.y = canvas.height + 4;
          s.x = Math.random() * canvas.width;
        }
        const px = s.x + mouse.x * 14 * s.z;
        const py = s.y + mouse.y * 14 * s.z;
        const alpha = 0.35 + 0.45 * Math.abs(Math.sin(s.tw)) * s.z;
        ctx.beginPath();
        ctx.arc(px, py, s.r * s.z, 0, Math.PI * 2);
        // occasional pink star
        ctx.fillStyle = s.tws > 0.03 ? `rgba(230, 0, 101, ${alpha})` : `rgba(255, 255, 255, ${alpha})`;
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMouse);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouse);
    };
}

// Animated canvas starfield with gentle parallax drift + mouse influence
export default function Starfield() {
  const canvasRef = useRef(null);

  // Mount-only: the animation manages its own lifecycle and cleanup
  useEffect(() => startStarfield(canvasRef.current), []);

  return <canvas ref={canvasRef} className="starfield-canvas" aria-hidden="true" />;
}
